import { useCallback } from 'react';

import { contractDownloadUrl, formDownloadUrl, libraryDocumentUrl } from './api';

import type { IPortalItemRow } from './types';

export const triggerDownload = (href: string, download = ''): void => {
  const link = document.createElement('a');
  link.href = href;
  link.download = download;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Download/preview helpers shared by the portal home sections. All build a
 * temporary `<a>` and click it — centralized so the row renderers stay focused
 * on layout.
 */
export const usePortalDownloads = (
  token: string
): {
  downloadContract: () => void;
  downloadLibraryDocument: (row: IPortalItemRow) => void;
  downloadUploadedAsset: (row: IPortalItemRow) => void;
  downloadForm: (row: IPortalItemRow) => void;
} => {
  const downloadContract = useCallback(() => {
    triggerDownload(contractDownloadUrl(token));
  }, [token]);

  const downloadLibraryDocument = useCallback(
    (row: IPortalItemRow) => {
      triggerDownload(libraryDocumentUrl(token, row.id));
    },
    [token]
  );

  const downloadUploadedAsset = useCallback(
    (row: IPortalItemRow) => {
      const latestAssetId = row.asset_ids?.[row.asset_ids.length - 1];
      if (!latestAssetId) return;
      triggerDownload(`/onboard/portal/${encodeURIComponent(token)}/assets/${latestAssetId}`);
    },
    [token]
  );

  const downloadForm = useCallback(
    (row: IPortalItemRow) => {
      triggerDownload(formDownloadUrl(token, row.id));
    },
    [token]
  );

  return {
    downloadContract,
    downloadLibraryDocument,
    downloadUploadedAsset,
    downloadForm
  };
};
